import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { axiosInstance } from '@/App';
import { ClipboardCheck } from 'lucide-react';

/** Corrective-action performance, computed from the tracked records (never AI estimates). */
export const CorrectiveActionMetrics = ({ scope = 'company', refreshKey = 0 }) => {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const path = scope === 'gc'
      ? '/gc/corrective-actions/metrics'
      : '/company/corrective-actions/metrics';
    axiosInstance.get(path)
      .then(({ data }) => { setMetrics(data); setError(false); })
      .catch(() => { setMetrics(null); setError(true); });
  }, [scope, refreshKey]);

  if (error) {
    return (
      <Card data-testid="corrective-action-metrics-error">
        <CardContent className="py-6 text-sm text-gray-500">
          {t('correctiveMetrics.unavailable')}
        </CardContent>
      </Card>
    );
  }

  if (!metrics) return null;

  const tiles = [
    { key: 'totalOpen', value: metrics.totalOpen, className: 'text-blue-700' },
    { key: 'overdue', value: metrics.overdue, className: 'text-red-700' },
    { key: 'pendingVerification', value: metrics.pendingVerification, className: 'text-purple-700' },
    { key: 'closed', value: metrics.closed, className: 'text-green-700' },
    {
      key: 'avgDaysToClosure',
      value: metrics.avgDaysToClosure === null ? '—' : metrics.avgDaysToClosure,
      className: 'text-gray-900',
    },
  ];

  return (
    <Card data-testid="corrective-action-metrics">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-blue-600" />
          {t('correctiveMetrics.title')}
        </CardTitle>
        <CardDescription>{t('correctiveMetrics.description')} · {t('correctiveMetrics.allTime')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {tiles.map((tile) => (
            <div key={tile.key} className="p-3 rounded-lg bg-gray-50" data-testid={`ca-metric-${tile.key}`}>
              <div className={`text-2xl font-semibold ${tile.className}`}>{tile.value}</div>
              <div className="text-xs text-gray-600 mt-1">{t(`correctiveMetrics.${tile.key}`)}</div>
            </div>
          ))}
        </div>

        {metrics.byGroup?.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">
              {t(metrics.groupedBy === 'contractor'
                ? 'correctiveMetrics.byContractor'
                : 'correctiveMetrics.byProject')}
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {metrics.byGroup.map((row) => (
                <div
                  key={row.location || row.contractor}
                  className="flex items-center justify-between gap-3 p-2 border rounded"
                  data-testid={`ca-metric-group-${row.location || row.contractor}`}
                >
                  <span className="text-sm truncate flex-1">{row.location || row.contractor}</span>
                  <div className="flex items-center gap-2 text-xs">
                    <Badge variant="outline">{t('correctiveMetrics.closureRate')}: {row.closureRate}%</Badge>
                    {row.overdue > 0 && (
                      <Badge className="bg-red-100 text-red-800">
                        {t('correctiveWorkflow.overdue')}: {row.overdue}
                      </Badge>
                    )}
                    <span className="text-gray-500">
                      {t('correctiveMetrics.avgDaysShort')}: {row.avgDaysToClosure ?? '—'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CorrectiveActionMetrics;
